import { useEffect, useMemo, useState } from "react";
import { Badge, Button, Empty, Modal, Tag, Tooltip, theme } from "antd";
import { TbAlertTriangle, TbCalendarStats, TbFileDescription, TbGauge, TbListCheck } from "react-icons/tb";
import { VERDICT_META, pctLabel, shortDate } from "./constants";
import { EvidencePanel, IntegrityPanel, MonthsPanel, SummaryPanel, TermsPanel } from "./panels";
import { ReconLoan, useMcaReconciliation } from "./hooks";

type SectionId = "summary" | "evidence" | "terms" | "months" | "integrity";

export interface ExpectedPaymentBreakdownProps {
  open: boolean;
  onClose: () => unknown;
  loan: ReconLoan | undefined;
  onUploadStatements?: () => void;
}

const PANELS = {
  summary: SummaryPanel,
  evidence: EvidencePanel,
  terms: TermsPanel,
  months: MonthsPanel,
  integrity: IntegrityPanel,
} as const;

export const ExpectedPaymentBreakdown = ({
  open,
  onClose,
  loan,
  onUploadStatements,
}: ExpectedPaymentBreakdownProps) => {
  const { token } = theme.useToken();
  const model = useMcaReconciliation(loan);
  const [section, setSection] = useState<SectionId>("summary");

  useEffect(() => {
    if (open) setSection("summary");
  }, [open, loan?.expected_payment_breakdown]);

  const hasPct = Boolean(loan?.receivables);
  const monthCount = model.result?.expected.breakdown.length ?? 0;

  const nav = useMemo(() => {
    const verdictMeta = VERDICT_META[model.verdict];
    const confidence = model.result?.coverage.confidence;

    return [
      {
        id: "summary" as SectionId,
        label: "Summary",
        icon: <TbGauge size={17} />,
        disabled: false,
        trailing: (
          <Tag
            color={
              verdictMeta.dot === "error"
                ? "red"
                : verdictMeta.dot === "warning"
                  ? "gold"
                  : verdictMeta.dot === "success"
                    ? "green"
                    : verdictMeta.dot === "processing"
                      ? "blue"
                      : "default"
            }
            style={{ marginInlineEnd: 0, fontSize: 11 }}
          >
            {verdictMeta.rail}
          </Tag>
        ),
      },
      {
        id: "evidence" as SectionId,
        label: "Evidence",
        icon: <TbListCheck size={17} />,
        disabled: !model.result,
        trailing: confidence ? (
          <Tag
            color={confidence === "high" ? "green" : confidence === "medium" ? "gold" : "red"}
            style={{ marginInlineEnd: 0, fontSize: 11, textTransform: "capitalize" }}
          >
            {confidence}
          </Tag>
        ) : null,
      },
      {
        id: "terms" as SectionId,
        label: "Terms and window",
        icon: <TbFileDescription size={17} />,
        disabled: false,
        trailing: hasPct ? (
          <span style={{ fontSize: 11, color: token.colorTextQuaternary, fontVariantNumeric: "tabular-nums" }}>
            {pctLabel(model.pctFraction)}
          </span>
        ) : null,
      },
      {
        id: "months" as SectionId,
        label: "Month by month",
        icon: <TbCalendarStats size={17} />,
        disabled: monthCount === 0,
        trailing: (
          <span style={{ fontSize: 11, color: token.colorTextQuaternary, fontVariantNumeric: "tabular-nums" }}>
            {monthCount}
          </span>
        ),
      },
      {
        id: "integrity" as SectionId,
        label: "Data integrity",
        icon: <TbAlertTriangle size={17} />,
        disabled: model.warnings.length === 0,
        trailing:
          model.blockingCount > 0 ? (
            <Badge count={model.blockingCount} color={token.colorError} />
          ) : model.warnings.length > 0 ? (
            <span style={{ fontSize: 11, color: token.colorTextQuaternary, fontVariantNumeric: "tabular-nums" }}>
              {model.warnings.length}
            </span>
          ) : null,
      },
    ];
  }, [model, hasPct, monthCount, token]);

  const Panel = PANELS[section];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={960}
      centered
      destroyOnHidden
      title={
        <div>
          <div style={{ fontSize: 18, fontWeight: 600 }}>Payment reconciliation</div>
          <div style={{ fontSize: 13, fontWeight: 400, color: token.colorTextTertiary }}>
            {loan
              ? `Funded ${shortDate(loan.funded_date)} · a percentage of receivables, against what was collected`
              : "No advance selected"}
          </div>
        </div>
      }
      footer={
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 12, color: token.colorTextTertiary }}>
            {model.blockingCount > 0
              ? `${model.blockingCount} thing${model.blockingCount === 1 ? "" : "s"} the calculation had to assume`
              : ""}
          </span>
          <div style={{ display: "flex", gap: 8 }}>
            {!model.expectedKnown && onUploadStatements ? (
              <Button type="primary" onClick={onUploadStatements}>
                Upload statements
              </Button>
            ) : null}
            <Button onClick={onClose}>Close</Button>
          </div>
        </div>
      }
      styles={{ body: { padding: 0 } }}
    >
      {!loan || !hasPct ? (
        <div style={{ padding: "48px 24px", borderTop: `1px solid ${token.colorBorderSecondary}` }}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="This advance has no specified percentage of receivables, so there is nothing to reconcile against."
          />
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            height: 540,
            borderTop: `1px solid ${token.colorBorderSecondary}`,
          }}
        >
          <nav
            style={{
              width: 236,
              flexShrink: 0,
              padding: "10px 8px",
              overflowY: "auto",
              borderRight: `1px solid ${token.colorBorderSecondary}`,
              background: token.colorFillQuaternary,
            }}
          >
            {nav.map((item) => {
              const active = item.id === section;
              const button = (
                <button
                  type="button"
                  aria-current={active}
                  disabled={item.disabled}
                  onClick={() => setSection(item.id)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    width: "100%",
                    padding: "9px 10px",
                    marginBottom: 2,
                    border: "none",
                    borderRadius: 10,
                    textAlign: "left",
                    cursor: item.disabled ? "not-allowed" : "pointer",
                    opacity: item.disabled ? 0.45 : 1,
                    background: active ? token.colorPrimaryBg : "transparent",
                    boxShadow: active ? `inset 0 0 0 1px ${token.colorPrimaryBorder}` : "none",
                    color: active ? token.colorPrimaryText : token.colorText,
                    transitionProperty: "background-color, box-shadow",
                    transitionDuration: "0.15s",
                    transitionTimingFunction: "cubic-bezier(0.2, 0, 0, 1)",
                  }}
                >
                  <span style={{ display: "inline-flex", flexShrink: 0 }}>{item.icon}</span>
                  <span
                    style={{
                      flex: 1,
                      minWidth: 0,
                      fontSize: 13,
                      fontWeight: active ? 500 : 400,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {item.label}
                  </span>
                  <span style={{ display: "inline-flex", alignItems: "center", flexShrink: 0 }}>{item.trailing}</span>
                </button>
              );

              return item.disabled ? (
                <Tooltip
                  key={item.id}
                  title={item.id === "integrity" ? "Nothing to report" : "Not available for this advance"}
                  placement="right"
                >
                  <div>{button}</div>
                </Tooltip>
              ) : (
                <div key={item.id}>{button}</div>
              );
            })}
          </nav>

          <div style={{ flex: 1, minWidth: 0 }}>
            <Panel loan={loan} model={model} onUploadStatements={onUploadStatements} />
          </div>
        </div>
      )}
    </Modal>
  );
};

export default ExpectedPaymentBreakdown;
